import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { buildEmployeeRecipientName } from "../../xml/employeeRecipientName";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { Record3922 } from "./interface/3922-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { formatDate } from "../../constants/formatDate";
import { parseAmount } from "../../constants/validationFunctions";

type Form3922DetailItem = {
  "@_xmlns": string;
  "@_xmlns:xsd": string;
  TaxYr: string;
  RecordId: string;
  IssuerOfficeCd?: string;
  VoidInd: "0";
  CorrectedInd: "0" | "1";
  PrevSubmittedRecRecipientGrp?: any;
  // TIN + TINSubmittedTypeCd + PersonName/BusinessName choice + MailingAddressGrp.
  // Built by the shared TY2026 split builder (recipientIdentity.ts) for layout-V2
  // records, or by the legacy inline shape below for pre-2026 records.
  RecipientDetail: object;
  RecipientAccountNum?: string;
  StockOptionGrantedDt: string;
  StockOptionExercisedDt: string;
  FMVPerShareOnGrantDtAmt?: string;
  FMVPerShareOnExerciseDtAmt?: string;
  ExercisePricePerShareAmt?: string;
  SharesTransferredCnt: string;
  LegalTitleTransferDt?: string;
  ExercisePricePerShrGrantDtAmt?: string;
};

export async function generate3922Xml(
  ctx: EngineContext,records: Record3922[]) {
  if (!records?.length) {
    throw new Error("No 3922 records supplied.");
  }

  const transmitterData = ctx.transmitter as any;
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: false,
  });

  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  const formDetails: Form3922DetailItem[] = records.map((rec) => {
    const recipientName = buildEmployeeRecipientName(rec as any);

    return {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear,
      RecordId: (rec.id).toString(),
      VoidInd: "0",
      CorrectedInd: rec.corrected ? "1" : "0",
      // TY2026 layout-V2 records file via the shared split builder (business as
      // BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      RecipientDetail: buildSplitRecipientDetail(
        rec as any,
        transmitterData.testFileIndicator === "T"
          ? "000000000"
          : (rec.RECIPIENT_ID_NUMBER || rec.EMPLOYEE_SSN || "").replace(
              /-/g,
              "",
            ),
        {
          USAddress: {
            AddressLine1Txt: rec.EMPLOYEE_ADDRESS_1,
            ...(rec.EMPLOYEE_ADDRESS_2 && {
              AddressLine2Txt: rec.EMPLOYEE_ADDRESS_2,
            }),
            ...(rec.EMPLOYEE_CITY && { CityNm: rec.EMPLOYEE_CITY }),
            ...(rec.EMPLOYEE_STATE && {
              StateAbbreviationCd: rec.EMPLOYEE_STATE,
            }),
            ...(rec.EMPLOYEE_ZIP && { ZIPCd: rec.EMPLOYEE_ZIP }),
          },
        }
      ) ?? {
        TIN:
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : // 3922 stores the single recipient identifier in RECIPIENT_ID_NUMBER
              // (canonical). Fall back to legacy EMPLOYEE_SSN for older records that
              // still carry the value there so existing e-files are unchanged.
              (rec.RECIPIENT_ID_NUMBER || rec.EMPLOYEE_SSN || "").replace(
                /-/g,
                "",
              ),
        ...recipientName,
        MailingAddressGrp: {
          USAddress: {
            AddressLine1Txt: rec.EMPLOYEE_ADDRESS_1,
            ...(rec.EMPLOYEE_ADDRESS_2 && {
              AddressLine2Txt: rec.EMPLOYEE_ADDRESS_2,
            }),
            ...(rec.EMPLOYEE_CITY && { CityNm: rec.EMPLOYEE_CITY }),
            ...(rec.EMPLOYEE_STATE && {
              StateAbbreviationCd: rec.EMPLOYEE_STATE,
            }),
            ...(rec.EMPLOYEE_ZIP && { ZIPCd: rec.EMPLOYEE_ZIP }),
          },
        },
      },
      ...(rec.EMPLOYEE_ACCOUNT_NO && {
        RecipientAccountNum: String(rec.EMPLOYEE_ACCOUNT_NO),
      }),

      // Required elements — default a blank date (incomplete rows) so the required element is always
      // present; an empty date is stripped and breaks the sequence (matches the 3921 generator).
      StockOptionGrantedDt: formatDate(rec.BOX1_DATE_GRANTED || new Date()),
      StockOptionExercisedDt: formatDate(rec.BOX2_DATE_EXERCISED || new Date()),
      ...(rec.BOX3_FAIR_MARKET_VALUE_GRANT_DATE && {
        FMVPerShareOnGrantDtAmt: parseAmount(
          rec.BOX3_FAIR_MARKET_VALUE_GRANT_DATE
        ),
      }),
      ...(rec.BOX4_FAIR_MARKET_VALUE_EXE_DATE && {
        FMVPerShareOnExerciseDtAmt: parseAmount(
          rec.BOX4_FAIR_MARKET_VALUE_EXE_DATE
        ),
      }),
      ...(rec.BOX5_EXERCISE_PRICE_PAID && {
        ExercisePricePerShareAmt: parseAmount(rec.BOX5_EXERCISE_PRICE_PAID),
      }),
      // IRS SharesTransferredCnt is an integer count — coerce "2.00"/"41.00" etc. to "2"/"41".
      SharesTransferredCnt: String(parseInt(String(rec.BOX6_NO_SHARES_TRANSFERRED ?? "0"), 10) || 0),
      ...(rec.BOX7_DATE_TITLE_TRANSFERRED && {
        LegalTitleTransferDt: formatDate(rec.BOX7_DATE_TITLE_TRANSFERRED),
      }),
      ...(rec.BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE && {
        ExercisePricePerShrGrantDtAmt: parseAmount(
          rec.BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE
        ),
      }),
    };
  });

  const submissionId = generateUniqueSubmissionId();
  const transmissionId = generateUniqueTransmissionId(ctx.irs.tcc);

  const xmlStructure = {
    "?xml": { "@_version": "1.0", "@_encoding": "UTF-8" },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",

      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: transmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator ?? "1",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator ?? "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator ?? "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: "1",
        TotalRecipientFormCnt: String(formDetails.length),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },

      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "3922",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: formatDate(new Date()),
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: String(formDetails.length),
            IRSubmission1FormTotals: {
              Form3922TotalAmtGrp: {
                FMVPerShareOnGrantDtAmt: records
                  .reduce(
                    (s, r) =>
                      s + (Number(r.BOX3_FAIR_MARKET_VALUE_GRANT_DATE) || 0),
                    0
                  )
                  .toFixed(2),
                FMVPerShareOnExerciseDtAmt: records
                  .reduce(
                    (s, r) =>
                      s + (Number(r.BOX4_FAIR_MARKET_VALUE_EXE_DATE) || 0),
                    0
                  )
                  .toFixed(2),
                ExercisePricePerShareAmt: records
                  .reduce(
                    (s, r) => s + (Number(r.BOX5_EXERCISE_PRICE_PAID) || 0),
                    0
                  )
                  .toFixed(2),
                ExercisePricePerShrGrantDtAmt: records
                  .reduce(
                    (s, r) =>
                      s +
                      (Number(r.BOX8_EXERCISE_PRICE_PAID_ON_GRANT_DATE) || 0),
                    0
                  )
                  .toFixed(2),
              },
            },
          },
          IRSubmission1Detail: { Form3922Detail: formDetails },
        },
      ],
    },
  };
  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}
